import { datadogRum } from '@datadog/browser-rum';
import styled from '@emotion/styled';
import { notify } from '@genteras/ui-lib/base';
import { Turnstile } from '@marsidev/react-turnstile';
import { Form } from 'antd';
import axios from 'axios';
import { useTranslation } from 'next-i18next';
import Router from 'next/router';
import Script from 'next/script';
import type { FC } from 'react';
import React, { useState } from 'react';
import { useSWRConfig } from 'swr';
import { popupFormCss } from '@/components/common/Form';
import { LanguageDropdown } from '@/components/common/LanguageDropdown';
import { commonConfig } from '@/lib/i18n/configs';
import { urls } from 'constant/urls';
import UserAPI from '../../lib/api/user';
import { StyledButton } from '../base/StyledButton';
import { StyledInput, StyledPassword } from '../base/StyledInput';

interface FormData {
  username: string;
  password: string;
}

type Props = {
  name: string;
  onSubmitSuccess?: () => void;
};

const LoginDiv = styled.div`
  margin-top: 20px;
`;

const SiteNameText = styled.h2`
  text-align: center;
  color: var(--Gold-Text);
  letter-spacing: 5px;
  font-weight: bold;
`;

const CenterButtonDiv = styled.div({
  justifyContent: 'center',
  display: 'flex',
});

const StyledCond = styled.div`
  width: 80%;
  max-width: 500px;
  background: rgb(26 26 26 / 60%);
  padding: 20px;
  border-radius: 15px;
`;

const FormItem = styled(Form.Item)({
  alignContent: 'center',
  padding: '2px',
  paddingBottom: '20px',
});

const LoginForm: FC<Props> = ({
  name,
  onSubmitSuccess = () => {
    return;
  },
}: Props) => {
  const { t } = useTranslation(commonConfig.i18nNamespaces);
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = useState(false);
  const { mutate } = useSWRConfig();
  const RECAPTCHA_SITE_KEY =
    process.env.NEXT_PUBLIC_CLOUDFLARE_TURNSTILE_SITE_KEY || '';
  const recaptchaRef = React.useRef();

  const handleSubmit = async (values: FormData) => {
    try {
      setIsLoading(true);
      // eslint-disable-next-line
      const token = (recaptchaRef.current as any)?.getResponse();
      const loginData = {
        username: values.username,
        password: values.password,
        types: ['Player'],
        recaptchaToken: token,
      };
      const data = await UserAPI.login(loginData);
      datadogRum.setUser({
        id: data.id,
      });
      mutate('/players/me', data);

      Router.push(urls.play);
      notify.success({
        message: t('common:LoginSuccess'),
      });
      onSubmitSuccess();
    } catch (e: unknown) {
      if (
        axios.isAxiosError(e) &&
        e?.response?.data?.message === 'Unauthorized'
      ) {
        notify.error({
          message: t('common:error.IncorrectUsernameOrPassword'),
          t,
        });
      } else if (
        axios.isAxiosError(e) &&
        e?.response?.data?.message == 'accountFrozen'
      ) {
        notify.error({
          message: t(e?.response?.data?.message),
          t,
        });
      } else if (
        axios.isAxiosError(e) &&
        e?.response?.data?.message === 'Low recaptcha score.'
      ) {
        notify.error({
          message: t('common:error.LOW_RECAPTCHA'),
          t,
        });
      } else {
        notify.error({
          message: t('common:error.GenericFailureMsg'),
          description:
            (axios.isAxiosError(e) && e?.response?.data?.message) ??
            t('common:SomethingWentWrong'),
          t,
        });
        throw e;
      }
    } finally {
      setIsLoading(false);
      // eslint-disable-next-line
      (recaptchaRef.current as any)?.reset();
    }
  };

  return (
    <LoginDiv>
      <Script
        src="https://challenges.cloudflare.com/turnstile/v0/api.js"
        async={true}
        defer={true}
      />
      <CenterButtonDiv>
        <StyledCond>
          <div style={{ width: '100%', height: '30px' }}>
            <div style={{ float: 'right' }}>
              <LanguageDropdown />
            </div>
          </div>
          <SiteNameText>{t('caesar.SiteName')}</SiteNameText>
          <Form
            form={form}
            css={popupFormCss}
            name={name}
            initialValues={{ remember: true }}
            onFinish={handleSubmit}
            autoComplete="off"
          >
            <FormItem
              name="username"
              rules={[
                {
                  required: true,
                  message: t('common:validation.pleaseInputYourUsername'),
                },
              ]}
            >
              <StyledInput
                size="large"
                style={{ height: '40px' }}
                placeholder={t('common:username')}
              />
            </FormItem>

            <FormItem
              name="password"
              rules={[
                {
                  required: true,
                  message: t('common:validation.pleaseInputYourPassword'),
                },
              ]}
            >
              <StyledPassword
                size="large"
                style={{ height: '40px' }}
                placeholder={t('common:password')}
              />
            </FormItem>

            <FormItem>
              <Turnstile
                ref={recaptchaRef}
                siteKey={RECAPTCHA_SITE_KEY}
                style={{ margin: '0px auto 10px' }}
              />
              <StyledButton
                style={{ width: '100%' }}
                htmlType="submit"
                loading={isLoading}
              >
                {t('common:header.login')}
              </StyledButton>
            </FormItem>
          </Form>
        </StyledCond>
      </CenterButtonDiv>
    </LoginDiv>
  );
};

export default LoginForm;
